import React from 'react';
import { isFuture } from 'date-fns';
import { AxiosError } from 'axios';
import { Offer, OfferDeed } from '../../../providers/api/dtos';
import { OfferDeedStatus } from '../../../domain/types';
import { removeNullAndUndefinedValues } from '../../../utils/array';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import useIntent from '../../../intent/hooks/use-intent/useIntent';
import useProperty from '../../../property/hooks/use-property/useProperty';

export type ComputedOfferDeedStatus = OfferDeedStatus;

export type UseComputedOfferDeedStatusData = {
  status: ComputedOfferDeedStatus,
  canBeUpdated: boolean,
  canBeUpdatedTo: (status: ComputedOfferDeedStatus) => boolean,
};

type UseComputedOfferDeedStatusResult = {
  data?: UseComputedOfferDeedStatusData | undefined,
  isLoading: boolean,
  errors?: Error[] | undefined,
};

export const computedOfferDeedStatusCanBeUpdatedTo = ({ date, status }: OfferDeed): ComputedOfferDeedStatus[] => {
  const canBeUpdatedTo: Record<ComputedOfferDeedStatus, ComputedOfferDeedStatus[]> = {
    [OfferDeedStatus.CONFIRMED]: [],
    [OfferDeedStatus.EXACT]: removeNullAndUndefinedValues([!isFuture(date) ? OfferDeedStatus.CONFIRMED : undefined]),
    [OfferDeedStatus.VAGUE]: removeNullAndUndefinedValues([!isFuture(date) ? OfferDeedStatus.CONFIRMED : undefined]),
  };

  return canBeUpdatedTo[status];
};

export function useComputedOfferDeedStatus(offer: Offer): UseComputedOfferDeedStatusResult {
  const [errors, setErrors] = React.useState<Error[] | undefined>();

  const handleNewError = React.useCallback((err: AxiosError) => {
    setErrors((prev) => {
      if (!prev) return [err];
      return [...prev, err];
    });
  }, []);

  const { userIsAdmin } = useRBAC();
  const { data: intent, isLoading: isIntentLoading } = useIntent(offer.intentId, { onError: handleNewError });
  const { data: property, isLoading: isPropertyLoading } = useProperty(intent?.propertyId, { onError: handleNewError });
  const userIsPropertyAgentOrManager = useCurrentAgentIsSameAgentOrManager(property?.agentEmail);

  const isLoading = React.useMemo(
    () => isIntentLoading || isPropertyLoading,
    [isIntentLoading, isPropertyLoading],
  );

  const canBeUpdatedToStatus = React.useMemo(() => {
    if (!(userIsAdmin || userIsPropertyAgentOrManager) || !offer.deed) {
      return [];
    }

    return computedOfferDeedStatusCanBeUpdatedTo(offer.deed);
  }, [offer.deed, userIsAdmin, userIsPropertyAgentOrManager]);

  const data: UseComputedOfferDeedStatusData | undefined = React.useMemo(() => {
    if (!offer.deed || errors || isLoading) return undefined;

    return {
      status: offer.deed.status,
      canBeUpdated: canBeUpdatedToStatus.length > 0,
      canBeUpdatedTo: (status: ComputedOfferDeedStatus) => canBeUpdatedToStatus.includes(status),
    };
  }, [canBeUpdatedToStatus, errors, isLoading, offer.deed]);

  return React.useMemo(() => ({
    data,
    isLoading,
    errors,
  }), [data, errors, isLoading]);
}
